import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { inputErrorState, inputFormValueState } from '../../stores/formState';
import { TranslateRule, translateValue } from './TranslateRules';
import { completedComponentState } from '../../stores/completedComponentState';
import { calcCharWidth } from '../../utils/calcCharWidth';
import { toFirstString } from '../../utils/toFirstString';

export interface NumberInputProps {
  componentId: string;
  name: string;
  size?: number;
  optional: boolean;
  message?: string;
  translateRule?: TranslateRule,
  maxLength?: number;
  max?: number;
  min?: number;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  focus?: boolean;
  autoComplete?: string;
  optionalTextHidden?: boolean;
}

const TextInput: React.FC<NumberInputProps> = props => {
  const [value, setValue] = useRecoilState(inputFormValueState(props.componentId));
  const [inputValue, setInputValue] = useState<string | null>();
  const [error, setError] = useRecoilState(inputErrorState(props.componentId));
  const inputRef = useRef<HTMLInputElement>(null);
  const [composingValue, setComposingValue] = useState<string | null>(null);

  useEffect(() => {
    if (value != null) {
      setInputValue(toFirstString(value));
    }
  }, [value]);


  const setTranslatedValue = useCallback((inputValue: string) => {
    if (props.optional && inputValue === '') {
      setInputValue(null);
      setValue(null);
      setError(null);
      return;
    }

    const [value, error] = translateValue(props.translateRule, inputValue);
    setInputValue(value != null ? value : inputValue);

    if (value != null) {
      const length = String(value).length;
      if (props.maxLength != null && length > props.maxLength) {
        setValue(null);
        setError(`${props.maxLength}文字まで入力可能です`)
        return;
      }
      if (props.max != null && parseInt(value) > props.max) {
        setValue(null);
        setError(`${props.max} 以下の値を入力してください`)
        return;
      }
      if (props.min != null && parseInt(value) < props.min) {
        setValue(null);
        setError(`${props.min} 以上の値を入力してください`)
        return;
      }
    }

    setValue(value);
    setError(error);
  }, [props.optional, props.translateRule, props.maxLength, props.max, props.min, setValue, setError])

  const onInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (composingValue != null) {
      setComposingValue(e.target.value);
    } else {
      setTranslatedValue(e.target.value);
    }
  }, [composingValue, setTranslatedValue]);

  useEffect(() => {
    if (props.focus && inputRef.current != null) {
      inputRef.current.focus();
    }
  }, [props.focus, inputRef])

  const setCompleted = useSetRecoilState(completedComponentState(props.componentId));
  const onBlur = useCallback(() => {
    if (value == null || value === '') {
      setCompleted(false)
    } else {
      setCompleted(true)
    }
  }, [value, setCompleted]);

  const onCompositionStart = useCallback((e: React.CompositionEvent<HTMLInputElement>) => {
    if (!(e.target instanceof HTMLInputElement)) return;
    setComposingValue(e.target.value)
  }, []);
  const onCompositionEnd = useCallback(() => {
    if (composingValue == null) return;
    setTranslatedValue(composingValue)
    setComposingValue(null);
  }, [composingValue, setTranslatedValue]);


  const valueStr = composingValue != null ? composingValue : inputValue != null ? String(inputValue) : '';
  const size = Math.max(props.size || 0, calcCharWidth(valueStr));

  return (
    <label className={`text-input ${props.translateRule || 'text'} ${error != null ? 'invalid' : ''}`}>
      <span>
      {props.message ? <span className="message">{props.message}</span> : ''}
      <span className="optional">({props.optional ? "任意" : "必須"})</span>
      </span>
      <span className="text-input-inner" data-prefix={props.prefix} data-suffix={props.suffix}>
        <input
          type="text"
          name={props.name}
          size={size}
          value={valueStr}
          placeholder={props.placeholder}
          autoComplete={props.autoComplete}
          onChange={onInput}
          onCompositionStart={onCompositionStart}
          onCompositionEnd={onCompositionEnd}
          onBlur={onBlur}
          ref={inputRef}/>
      </span>
      <span className="error-message">{error}</span>
    </label>
  );
};

export default TextInput;
